import React, { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useAudioStateContext } from "hooks/useAudioStateContext";
import { useAudioHandlesContext } from "hooks/useAudioHandlesContext";
import { AudioTrack } from "providers/AudioProvider/types";

const cols = 79;

interface ITranslatedSplitModalProps {
    oneTrack: AudioTrack;
    handleClose: () => void;
}

const tokensOf = (text: string) => text.split(/\s+/).filter(Boolean);

/**
 * Translated-led split: the translator marks break points with Enter inside the
 * translated text; the backend splits the pair (and the RU counterpart) at those
 * points and re-voices the parts. The text itself may not change — only line breaks.
 */
export const TranslatedSplitModal: React.FC<ITranslatedSplitModalProps> = ({
    oneTrack,
    handleClose,
}) => {
    const handles = useAudioHandlesContext();
    const state = useAudioStateContext();
    const original = oneTrack.translation || "";
    const [text, setText] = useState(original);
    const [submitting, setSubmitting] = useState(false);

    const tokensMatch = useMemo(
        () => tokensOf(text).join(" ") === tokensOf(original).join(" "),
        [text, original],
    );
    const parts = text
        .split("\n")
        .map((s) => s.trim())
        .filter(Boolean);
    const canConfirm = tokensMatch && parts.length > 1 && !submitting && !state.savingStatus;

    const handleConfirm = async () => {
        if (!canConfirm) {
            return;
        }
        setSubmitting(true);
        try {
            await handles.splitTranslatedSubtitle({
                id: oneTrack.original_id,
                folder_num: parseInt(oneTrack.folder_num),
                translated_parts: parts,
            });
        } finally {
            setSubmitting(false);
        }
        handleClose();
    };

    // Portal to body: SubtitleContainerSC is its own z-index:1 stacking context, so an
    // overlay rendered inside it would paint below later sibling tracks (VideoPlayer
    // escapes the same way).
    return createPortal(
        <div className="p0 active">
            <div className="p_b active">
                <div className="p_b_top">
                    <div className="p_b_title">
                        <div className="ico">content_cut</div>
                        Разбить по переводу
                    </div>
                    <div className="p_b_x" onClick={handleClose}>
                        <div className="ico">
                            <div>close</div>
                        </div>
                    </div>
                </div>
                <div className="p_b_c">
                    <div className="p_b_text">
                        <div className="p_b_textarea00">
                            <div className="p_b_textarea0">
                                <textarea
                                    style={{ minHeight: "9em" }}
                                    cols={cols}
                                    value={text}
                                    onChange={(e) => setText(e.target.value)}
                                    maxLength={5000}
                                    className="p_b_textarea w-input"
                                    autoFocus
                                />
                            </div>
                        </div>
                    </div>

                    <div className="p_b_note">
                        <div>
                            ☝️ Enter = точка разбиения. Текст менять нельзя — только переносы
                            строк.
                        </div>
                    </div>

                    {oneTrack.captions && (
                        <div
                            style={{
                                fontSize: "0.85em",
                                color: "#666",
                                padding: "0.5em 0",
                                whiteSpace: "pre-wrap",
                            }}
                        >
                            {oneTrack.captions}
                        </div>
                    )}

                    {!tokensMatch && (
                        <div style={{ color: "red", fontSize: "0.85em" }}>
                            Текст изменён — разрешены только переносы строк.
                        </div>
                    )}
                    {state.translatedOpError && (
                        <div style={{ color: "red", fontSize: "0.85em" }}>
                            {state.translatedOpError}
                        </div>
                    )}

                    <div className="p_b_bot">
                        <div className="x_save x_save--cancel" onClick={handleClose}>
                            Отмена
                        </div>
                        <button
                            className={canConfirm ? "x_save" : "x_save x_save--cancel"}
                            style={{ opacity: canConfirm ? 1 : 0.3 }}
                            disabled={!canConfirm}
                            onClick={handleConfirm}
                        >
                            {submitting ? "Wait..." : "Разбить"}
                        </button>
                    </div>
                </div>
            </div>

            <div onClick={handleClose} className="p_z active" />
        </div>,
        document.body,
    );
};
